import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { CheckCircle2, Clock, FileText, Pencil, Plus, Trash2, X } from 'lucide-react';
import { fetchCollegeData } from '../helpers/collegeAPI';
import { getAutocompleteSuggestions } from '../helpers/smartSearch';
import { usePersistedState } from '../helpers/useDashboardStorage';
import { userStorage } from '../lib/userStorage';
import {
  DEFAULT_PROGRESS_SOURCE,
  getDashboardProgressColleges,
  getManualProgressCollegeObjects,
  getProgressMetaKey,
  getProgressTasksKey,
  MANUAL_PROGRESS_COLLEGES_KEY,
  PROGRESS_SOURCE_KEY,
} from '../helpers/progressSource';
import { useNotifications, REMINDER_OFFSETS } from '../context/NotificationContext';
import {
  createGeneralProgressCards,
  GENERAL_PROGRESS_CARDS_KEY,
  reconcileGeneralProgressSchedule,
} from '../helpers/generalProgressSchedule';
import './ProgressPage.css';

const PRIORITY_OPTIONS = ['Low', 'Medium', 'High'];
const REMINDER_OPTIONS = Object.entries(REMINDER_OFFSETS).map(([value, { label }]) => ({ value, label }));
/* ─── Single Task Row ─── */
function TaskItem({ task, onToggleStatus, onEdit, onDelete }) {
  const [hovering, setHovering] = useState(false);
  const isComplete = task.status === 'complete';
  const isInProgress = task.status === 'in-progress';

  // Determine what the circle click does:
  // todo → in-progress → complete → todo (cycle)
  const handleCircleClick = () => {
    if (isComplete) {
      onToggleStatus(task.id, 'todo');
    } else {
      onToggleStatus(task.id, 'complete');
    }
  };

  const priorityClass = task.priority ? `task-priority--${task.priority.toLowerCase()}` : '';

  return (
    <div
      className={`task-item ${isComplete ? 'task-item--complete' : ''}`}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {/* Drag handle dots */}
      <span className="task-item__grip" title="Drag to reorder">⠿</span>

      {/* Circle / checkmark */}
      <button
        className={`task-item__circle ${isComplete ? 'task-item__circle--checked' : ''} ${isInProgress ? 'task-item__circle--in-progress' : ''}`}
        onClick={handleCircleClick}
        title={isComplete ? 'Mark incomplete' : 'Mark complete'}
      >
        {isComplete && (
          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
            <path d="M1 4L3.5 6.5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
        {isInProgress && !isComplete && (
          <span className="task-item__circle-dot" />
        )}
      </button>

      {/* Task text */}
      <div className="task-item__body">
        <span className={`task-item__text ${isComplete ? 'task-item__text--struck' : ''}`}>
          {task.text}
        </span>
        <div className="task-item__meta">
          {task.date && (
            <span className="task-item__date">{task.date}</span>
          )}
          {task.priority && (
            <span className={`task-item__priority ${priorityClass}`}>
              {task.priority}
            </span>
          )}
          {task.reminderOffset && (
            <span className="task-item__reminder-badge">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              {REMINDER_OFFSETS[task.reminderOffset]?.label || task.reminderOffset}
            </span>
          )}
          {isInProgress && (
            <span className="task-item__status-badge task-item__status-badge--in-progress">
              In Progress
            </span>
          )}
        </div>
      </div>

      {/* Hover actions */}
      <div className={`task-item__actions ${hovering ? 'task-item__actions--visible' : ''}`}>
        <button className="task-item__action-btn" onClick={() => onEdit(task)} title="Edit task">
          <Pencil size={12} strokeWidth={2} />
        </button>
        <button className="task-item__action-btn task-item__action-btn--delete" onClick={() => onDelete(task.id)} title="Delete task">
          <X size={13} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}

/* ─── Add Task Form (inline) ─── */
function AddTaskForm({ onAdd, onCancel }) {
  const [text, setText] = useState('');
  const [date, setDate] = useState('');
  const [priority, setPriority] = useState('');
  const [reminderOffset, setReminderOffset] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = () => {
    if (!text.trim()) return;
    onAdd({
      id: Date.now(),
      text: text.trim(),
      date,
      priority,
      reminderOffset: reminderOffset || null,
      status: 'todo',
    });
    setText('');
    setDate('');
    setPriority('');
    setReminderOffset('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit();
    if (e.key === 'Escape') onCancel();
  };

  return (
    <div className="add-task-form">
      <div className="add-task-form__row">
        <input
          ref={inputRef}
          className="add-task-form__input"
          type="text"
          placeholder="Task name"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </div>
      <div className="add-task-form__options">
        <input
          className="add-task-form__date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <select
          className="add-task-form__priority"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
        >
          <option value="">Priority</option>
          {PRIORITY_OPTIONS.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <select
          className="add-task-form__reminder"
          value={reminderOffset}
          onChange={(e) => setReminderOffset(e.target.value)}
        >
          <option value="">Reminder</option>
          {REMINDER_OPTIONS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
        <div className="add-task-form__btns">
          <button className="add-task-form__submit" onClick={handleSubmit}>Add</button>
          <button className="add-task-form__cancel" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Edit Task Form (inline) ─── */
function EditTaskForm({ task, onSave, onCancel }) {
  const [text, setText] = useState(task.text);
  const [date, setDate] = useState(task.date || '');
  const [priority, setPriority] = useState(task.priority || '');
  const [status, setStatus] = useState(task.status || 'todo');
  const [reminderOffset, setReminderOffset] = useState(task.reminderOffset || '');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleSubmit = () => {
    if (!text.trim()) return;
    onSave({ ...task, text: text.trim(), date, priority, status, reminderOffset: reminderOffset || null });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit();
    if (e.key === 'Escape') onCancel();
  };

  return (
    <div className="add-task-form">
      <div className="add-task-form__row">
        <input
          ref={inputRef}
          className="add-task-form__input"
          type="text"
          placeholder="Task name"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </div>
      <div className="add-task-form__options">
        <input
          className="add-task-form__date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <select
          className="add-task-form__priority"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
        >
          <option value="">Priority</option>
          {PRIORITY_OPTIONS.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <select
          className="add-task-form__status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="todo">To Do</option>
          <option value="in-progress">In Progress</option>
          <option value="complete">Complete</option>
        </select>
        <select
          className="add-task-form__reminder"
          value={reminderOffset}
          onChange={(e) => setReminderOffset(e.target.value)}
        >
          <option value="">Reminder</option>
          {REMINDER_OPTIONS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
        <div className="add-task-form__btns">
          <button className="add-task-form__submit" onClick={handleSubmit}>Save</button>
          <button className="add-task-form__cancel" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export function GeneralProgressCard({ card, onToggleItem, onUpdateItem, onAddItem, onDeleteItem, onDeleteCard }) {
  const [expanded, setExpanded] = useState(false);
  const [draftItem, setDraftItem] = useState('');
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [editingDate, setEditingDate] = useState('');
  const [editingReminder, setEditingReminder] = useState('');
  const completeCount = card.items.filter((item) => item.complete).length;
  const isComplete = card.items.length > 0 && completeCount === card.items.length;
  const nextItem = card.items.find((item) => !item.complete) || null;
  const panelId = React.useId();

  const formatNextDate = (date) => {
    if (!date) return '';
    return new Date(`${date}T00:00:00`).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const startEdit = (item) => {
    setEditingItemId(item.id);
    setEditingText(item.text);
    setEditingDate(item.date || '');
    setEditingReminder(item.reminderOffset || '');
  };

  const cancelEdit = () => {
    setEditingItemId(null);
    setEditingText('');
    setEditingDate('');
    setEditingReminder('');
  };

  const saveEdit = () => {
    if (!editingText.trim()) return;
    onUpdateItem(card.id, editingItemId, {
      text: editingText.trim(),
      date: editingDate,
      reminderOffset: editingDate ? (editingReminder || null) : null,
    });
    cancelEdit();
  };

  const handleAddItem = () => {
    if (!draftItem.trim()) return;
    onAddItem(card.id, draftItem.trim());
    setDraftItem('');
  };

  return (
    <article className={`general-progress-card ${isComplete ? 'general-progress-card--complete' : ''}`}>
      <div className="general-progress-card__header">
        <button
          type="button"
          className="general-progress-card__toggle"
          onClick={() => setExpanded((current) => !current)}
          aria-expanded={expanded}
          aria-controls={panelId}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${card.title}`}
        >
          <span className={`general-progress-card__chevron ${expanded ? 'general-progress-card__chevron--open' : ''}`} aria-hidden="true">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M4 2L8 6L4 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className="general-progress-card__heading">
            <span className="general-progress-card__title-row">
              <strong>{card.title}</strong>
              <span className="general-progress-card__count">{completeCount}/{card.items.length}</span>
            </span>
            {expanded && <span className="general-progress-card__description">{card.description}</span>}
          </span>
        </button>
        <button
          type="button"
          className="general-progress-card__delete"
          onClick={() => onDeleteCard(card.id)}
          aria-label={`Delete ${card.title}`}
          title="Delete card"
        >
          <Trash2 size={15} strokeWidth={1.8} />
        </button>
      </div>

      {!expanded && (
        <div className={`general-progress-card__next ${isComplete ? 'general-progress-card__next--complete' : ''}`}>
          {nextItem ? (
            <>
              <button
                type="button"
                className="general-progress-item__check general-progress-card__next-check"
                onClick={() => onToggleItem(card.id, nextItem.id)}
                aria-label={`Mark ${nextItem.text} complete`}
              />
              <div className="general-progress-card__next-copy">
                <span>Next step</span>
                <strong>{nextItem.text}</strong>
              </div>
              {nextItem.date && <time dateTime={nextItem.date}>{formatNextDate(nextItem.date)}</time>}
            </>
          ) : (
            <div className="general-progress-card__next-copy">
              <span>{card.items.length ? 'Complete' : 'No steps yet'}</span>
              <strong>{card.items.length ? 'All steps finished' : 'Expand this card to add a step'}</strong>
            </div>
          )}
        </div>
      )}

      <div
        id={panelId}
        className="general-progress-card__details"
        role="region"
        aria-label={`${card.title} steps`}
        hidden={!expanded}
      >
        {expanded && (
          <>
            <div className="general-progress-card__items">
              {card.items.map((item) => (
          <div className={`general-progress-item ${item.complete ? 'general-progress-item--complete' : ''}`} key={item.id}>
            <button
              type="button"
              className={`general-progress-item__check ${item.complete ? 'general-progress-item__check--checked' : ''}`}
              onClick={() => onToggleItem(card.id, item.id)}
              aria-label={item.complete ? `Mark ${item.text} incomplete` : `Mark ${item.text} complete`}
            >
              {item.complete && <CheckCircle2 size={14} strokeWidth={2.2} />}
            </button>
            {editingItemId === item.id ? (
              <div className="general-progress-item__editor">
                <input
                  className="general-progress-item__edit"
                  value={editingText}
                  onChange={(event) => setEditingText(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') saveEdit();
                    if (event.key === 'Escape') cancelEdit();
                  }}
                  autoFocus
                />
                <div className="general-progress-item__schedule">
                  <label>
                    <span>Suggested target</span>
                    <input
                      type="date"
                      value={editingDate}
                      onChange={(event) => setEditingDate(event.target.value)}
                    />
                  </label>
                  <label>
                    <span>Reminder</span>
                    <select
                      value={editingReminder}
                      onChange={(event) => setEditingReminder(event.target.value)}
                      disabled={!editingDate}
                    >
                      <option value="">None</option>
                      {REMINDER_OPTIONS.map((reminder) => (
                        <option key={reminder.value} value={reminder.value}>{reminder.label}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="general-progress-item__editor-actions">
                  <button type="button" className="general-progress-item__save" onClick={saveEdit}>Save</button>
                  <button type="button" className="general-progress-item__cancel" onClick={cancelEdit}>Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <span className="general-progress-item__text">{item.text}</span>
                <div className="general-progress-item__actions">
                  <button
                    type="button"
                    className="general-progress-item__edit-button"
                    onClick={() => startEdit(item)}
                    aria-label={`Edit ${item.text}`}
                    title="Edit step"
                  >
                    <Pencil size={12} strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    className="general-progress-item__delete"
                    onClick={() => onDeleteItem(card.id, item.id)}
                    aria-label={`Delete ${item.text}`}
                    title="Delete step"
                  >
                    <X size={13} strokeWidth={2} />
                  </button>
                </div>
              </>
            )}
          </div>
              ))}
            </div>

            <div className="general-progress-card__add-row">
              <input
                value={draftItem}
                onChange={(event) => setDraftItem(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') handleAddItem();
                }}
                placeholder="Add another step"
              />
              <button type="button" onClick={handleAddItem}>
                <Plus size={14} strokeWidth={2} />
                Add
              </button>
            </div>
          </>
        )}
      </div>
    </article>
  );
}

/* ─── College Card with expandable todo list ─── */
const APP_TYPE_OPTIONS = ['Early Action', 'Early Decision', 'Early Decision II', 'Rolling', 'Regular Decision'];

export function CollegeCard({ college, onUpdate, onTaskChange, onRemove }) {
  const [expanded, setExpanded] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  // Persist tasks per-college
  const storageKey = getProgressTasksKey(college.name);
  const [tasks, setTasks] = usePersistedState(storageKey, []);

  // Persist apply-by date and application type
  const metaKey = getProgressMetaKey(college.name);
  const [meta, setMeta] = usePersistedState(metaKey, {
    applyByDate: '',
    applicationType: '',
    applicationSubmitted: false,
  });
  const isSubmitted = Boolean(meta.applicationSubmitted);

  // Notify parent so progress, sorting, and notifications update instantly
  useEffect(() => {
    if (onUpdate) onUpdate();
    if (onTaskChange) onTaskChange();
  }, [tasks, meta.applyByDate, meta.applicationType, meta.applicationSubmitted, onUpdate, onTaskChange]);

  const handleMetaChange = (field, value) => {
    setMeta((prev) => ({ ...prev, [field]: value }));
  };

  // Split into incomplete (top) and complete (bottom)
  const incompleteTasks = tasks.filter((t) => t.status !== 'complete');
  const completeTasks = tasks.filter((t) => t.status === 'complete');

  const handleAddTask = (newTask) => {
    setTasks((prev) => [...prev, newTask]);
    setShowAddForm(false);
  };

  const handleToggleStatus = (taskId, newStatus) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t))
    );
  };

  const handleEditSave = (updatedTask) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === updatedTask.id ? updatedTask : t))
    );
    setEditingTask(null);
  };

  const handleDeleteTask = (taskId) => {
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
  };

  const taskCount = tasks.length;
  const doneCount = completeTasks.length;
  const inProgressCount = tasks.filter((t) => t.status === 'in-progress').length;
  const todoCount = taskCount - doneCount - inProgressCount;

  // Percentages for the segmented progress bar
  const pctComplete = taskCount > 0 ? (doneCount / taskCount) * 100 : 0;
  const pctInProgress = taskCount > 0 ? (inProgressCount / taskCount) * 100 : 0;
  const pctTodo = taskCount > 0 ? (todoCount / taskCount) * 100 : 0;

  // Format apply-by date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Days until deadline
  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const target = new Date(dateStr + 'T00:00:00');
    const diff = Math.ceil((target - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const daysUntil = getDaysUntil(meta.applyByDate);
  const taskPanelId = React.useId();
  const toggleTaskPanel = () => setExpanded((current) => !current);

  return (
    <article
      className={`progress-card ${expanded ? 'progress-card--expanded' : ''}`}
      aria-label={`${college.name} application`}
    >
      {/* Card header — single row */}
      <div className="progress-card__header">
        <button
          type="button"
          className="progress-card__header-left progress-card__header-toggle"
          onClick={toggleTaskPanel}
          aria-expanded={expanded}
          aria-controls={taskPanelId}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} tasks for ${college.name}`}
        >
          <span className={`progress-card__chevron ${expanded ? 'progress-card__chevron--open' : ''}`} aria-hidden="true">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M4 2L8 6L4 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className="progress-card__name">{college.name}</span>

          {/* Days left badge next to college name */}
          {isSubmitted && (
            <span className="progress-card__submitted-badge">
              <CheckCircle2 size={12} />
              Submitted
            </span>
          )}

          {!isSubmitted && meta.applyByDate && daysUntil !== null && daysUntil <= 30 && (
            <span className={`progress-card__days-badge ${daysUntil <= 7 && daysUntil >= 0 ? 'progress-card__days-badge--urgent' : ''} ${daysUntil < 0 ? 'progress-card__days-badge--past' : ''}`}>
              {daysUntil < 0 ? 'Past due' : daysUntil === 0 ? 'Due today' : `${daysUntil}d left`}
            </span>
          )}

          {college.category && (
            <span className={`progress-card__badge progress-card__badge--${college.category.toLowerCase()}`}>
              {college.category}
            </span>
          )}
        </button>
        <div className="progress-card__header-right">
          {onRemove && (
            <button
              type="button"
              className="progress-card__remove-btn"
              onClick={() => onRemove(college.name)}
              aria-label={`Remove ${college.name} from Individual Progress`}
              title="Remove from manual progress"
            >
              <X size={14} />
            </button>
          )}

          <button
            type="button"
            className={`progress-card__submitted-toggle ${isSubmitted ? 'progress-card__submitted-toggle--active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleMetaChange('applicationSubmitted', !isSubmitted);
            }}
            aria-pressed={isSubmitted}
            title={isSubmitted ? 'Mark application not submitted' : 'Mark application submitted'}
          >
            <CheckCircle2 size={13} />
            <span>{isSubmitted ? 'Submitted' : 'Mark submitted'}</span>
          </button>

          {/* App type pill */}
          <select
            className={`progress-card__pill ${meta.applicationType ? 'progress-card__pill--set' : ''}`}
            value={meta.applicationType}
            onChange={(e) => handleMetaChange('applicationType', e.target.value)}
            aria-label={`Application type for ${college.name}`}
          >
            <option value="">Type</option>
            {APP_TYPE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}


          </select>

          {/* Date pill — hidden native picker */}
          <div
            className={`progress-card__pill progress-card__pill--date ${meta.applyByDate ? 'progress-card__pill--set' : ''}`}
          >
            <span className="progress-card__pill-text" aria-hidden="true">
              {meta.applyByDate ? formatDate(meta.applyByDate) : 'Apply by'}
            </span>
            <input
              type="date"
              className="progress-card__date-hidden"
              value={meta.applyByDate}
              onChange={(e) => handleMetaChange('applyByDate', e.target.value)}
              aria-label={`Application deadline for ${college.name}`}
              title="Set apply-by date"
            />
          </div>



          {/* Task counter */}
          <button
            type="button"
            className="progress-card__counter"
            onClick={toggleTaskPanel}
            aria-expanded={expanded}
            aria-controls={taskPanelId}
            aria-label={`${doneCount} of ${taskCount} tasks complete for ${college.name}. ${expanded ? 'Collapse' : 'Expand'} tasks.`}
          >
            {doneCount}/{taskCount}
          </button>
        </div>
      </div>

      {/* Segmented progress bar — always visible */}
      {taskCount > 0 && (
        <div className="progress-card__bar-wrapper">
          <div className="progress-card__bar">
            {pctComplete > 0 && (
              <div className="progress-card__bar-segment progress-card__bar-segment--complete" style={{ width: `${pctComplete}%` }} />
            )}
            {pctInProgress > 0 && (
              <div className="progress-card__bar-segment progress-card__bar-segment--in-progress" style={{ width: `${pctInProgress}%` }} />
            )}
            {pctTodo > 0 && (
              <div className="progress-card__bar-segment progress-card__bar-segment--todo" style={{ width: `${pctTodo}%` }} />
            )}
          </div>
        </div>
      )}

      {/* Expandable task list */}
      <div
        id={taskPanelId}
        className="progress-card__tasks"
        role="region"
        aria-label={`Tasks for ${college.name}`}
        hidden={!expanded}
      >
        {expanded && (
          <>
          {/* Incomplete tasks */}
          {incompleteTasks.map((task) =>
            editingTask?.id === task.id ? (
              <EditTaskForm
                key={task.id}
                task={task}
                onSave={handleEditSave}
                onCancel={() => setEditingTask(null)}
              />
            ) : (
              <TaskItem
                key={task.id}
                task={task}
                onToggleStatus={handleToggleStatus}
                onEdit={setEditingTask}
                onDelete={handleDeleteTask}
              />
            )
          )}

          {/* Add task form or button */}
          {showAddForm ? (
            <AddTaskForm onAdd={handleAddTask} onCancel={() => setShowAddForm(false)} />
          ) : (
            <button className="progress-card__add-btn" onClick={() => setShowAddForm(true)}>
              <span className="progress-card__add-icon">+</span>
              Add task
            </button>
          )}

          {/* Completed tasks at bottom */}
          {completeTasks.length > 0 && (
            <div className="progress-card__completed-section">
              <span className="progress-card__completed-label">
                Completed ({completeTasks.length})
              </span>
              {completeTasks.map((task) =>
                editingTask?.id === task.id ? (
                  <EditTaskForm
                    key={task.id}
                    task={task}
                    onSave={handleEditSave}
                    onCancel={() => setEditingTask(null)}
                  />
                ) : (
                  <TaskItem
                    key={task.id}
                    task={task}
                    onToggleStatus={handleToggleStatus}
                    onEdit={setEditingTask}
                    onDelete={handleDeleteTask}
                  />
                )
              )}
            </div>
          )}
          </>
        )}
      </div>
    </article>
  );
}

/** Read application-level counts across the student's final college list. */
function getApplicationStats(colleges) {
  let submitted = 0;

  colleges.forEach((college) => {
    const storageKey = getProgressMetaKey(college.name);
    try {
      const raw = userStorage.getItem(storageKey);
      if (!raw) return;
      const meta = JSON.parse(raw);
      if (meta?.applicationSubmitted) submitted += 1;
    } catch {
      // skip
    }
  });

  return {
    total: colleges.length,
    inProgress: colleges.length - submitted,
    submitted,
  };
}

/**
 * Sort colleges based on persisted metadata.
 */
function sortColleges(colleges, sortBy) {
  const getMeta = (college) => {
    const key = getProgressMetaKey(college.name);
    try {
      const raw = userStorage.getItem(key);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  };

  const getTasks = (college) => {
    const key = getProgressTasksKey(college.name);
    try {
      const raw = userStorage.getItem(key);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  };

  const sorted = [...colleges];

  switch (sortBy) {
    case 'alpha':
      sorted.sort((a, b) => a.name.localeCompare(b.name));
      break;
    case 'deadline': {
      sorted.sort((a, b) => {
        const da = getMeta(a).applyByDate || '';
        const db = getMeta(b).applyByDate || '';
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        return da.localeCompare(db);
      });
      break;
    }
    case 'completion': {
      sorted.sort((a, b) => {
        const tasksA = getTasks(a);
        const tasksB = getTasks(b);
        const pctA = tasksA.length > 0 ? tasksA.filter(t => t.status === 'complete').length / tasksA.length : 0;
        const pctB = tasksB.length > 0 ? tasksB.filter(t => t.status === 'complete').length / tasksB.length : 0;
        return pctA - pctB;
      });
      break;
    }
    case 'app-type': {
      const typeOrder = { 'Early Decision': 0, 'Early Decision II': 1, 'Early Action': 2, 'Rolling': 3, 'Regular Decision': 4 };
      sorted.sort((a, b) => {
        const ta = getMeta(a).applicationType || '';
        const tb = getMeta(b).applicationType || '';
        const oa = typeOrder[ta] ?? 99;
        const ob = typeOrder[tb] ?? 99;
        return oa - ob;
      });
      break;
    }
    default:
      break;
  }

  return sorted;
}

const SORT_OPTIONS = [
  { value: 'alpha', label: 'A → Z' },
  { value: 'deadline', label: 'Deadline' },
  { value: 'completion', label: 'Completion' },
  { value: 'app-type', label: 'App Type' },
];

/* ─── Main Progress Page ─── */
function ProgressPage({ dashboards }) {
  const location = useLocation();
  const requestedTab = new URLSearchParams(location.search).get('tab');
  const [activeTab, setActiveTab] = useState(
    ['final-list', 'general', 'individual'].includes(requestedTab) ? requestedTab : 'final-list'
  );
  const [sortBy, setSortBy] = useState('alpha');
  const [refreshKey, setRefreshKey] = useState(0);
  const [progressSource, setProgressSource] = usePersistedState(PROGRESS_SOURCE_KEY, DEFAULT_PROGRESS_SOURCE);
  const [manualColleges, setManualColleges] = usePersistedState(MANUAL_PROGRESS_COLLEGES_KEY, []);
  const [collegeData, setCollegeData] = useState(null);
  const [manualInputValue, setManualInputValue] = useState('');
  const [selectedManualCollege, setSelectedManualCollege] = useState('');
  const [manualSuggestions, setManualSuggestions] = useState([]);
  const [manualActionMessage, setManualActionMessage] = useState('');
  const [collegeLoading, setCollegeLoading] = useState(true);
  const [collegeLoadError, setCollegeLoadError] = useState(false);
  const graduationYear = useMemo(() => {
    try {
      const personalInfo = JSON.parse(userStorage.getItem('my_info_personal') || '{}');
      return personalInfo.graduationYearConfirmed
        ? Number(personalInfo.graduationYear) || null
        : null;
    } catch {
      return null;
    }
  }, []);
  const defaultGeneralCards = useMemo(
    () => createGeneralProgressCards(graduationYear),
    [graduationYear]
  );
  const [generalCards, setGeneralCards] = usePersistedState(GENERAL_PROGRESS_CARDS_KEY, defaultGeneralCards);
  const [newGeneralCardTitle, setNewGeneralCardTitle] = useState('');
  const manualSearchDebounceRef = useRef(null);
  const { checkReminders } = useNotifications();

  useEffect(() => {
    if (['final-list', 'general', 'individual'].includes(requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [requestedTab]);

  useEffect(() => {
    const reconciledCards = reconcileGeneralProgressSchedule();
    setGeneralCards((currentCards) => (
      JSON.stringify(currentCards) === JSON.stringify(reconciledCards)
        ? currentCards
        : reconciledCards
    ));
  }, [setGeneralCards]);

  const triggerRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!progressSource || !['none', 'dashboard', 'manual'].includes(progressSource.mode)) {
      setProgressSource(DEFAULT_PROGRESS_SOURCE);
      return;
    }

    if (progressSource.mode === 'dashboard' && progressSource.dashboardId) {
      const syncedColleges = getDashboardProgressColleges(dashboards, progressSource.dashboardId)
        .map((college) => college.name)
        .filter(Boolean);

      if (syncedColleges.length) {
        setManualColleges((prev) => [...new Set([...prev, ...syncedColleges])]);
      }

      setProgressSource(DEFAULT_PROGRESS_SOURCE);
      return;
    }

    if (progressSource.mode === 'none') {
      setProgressSource(DEFAULT_PROGRESS_SOURCE);
    }
  }, [dashboards, progressSource, setManualColleges, setProgressSource]);

  useEffect(() => {
    let cancelled = false;
    setCollegeLoading(true);
    setCollegeLoadError(false);

    fetchCollegeData()
      .then((data) => {
        if (cancelled) return;
        if (!data?.results) {
          setCollegeData({ results: [] });
          setCollegeLoadError(true);
          return;
        }
        setCollegeData(data);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Error fetching college data:', error);
        setCollegeData({ results: [] });
        setCollegeLoadError(true);
      })
      .finally(() => {
        if (!cancelled) {
          setCollegeLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (manualSearchDebounceRef.current) clearTimeout(manualSearchDebounceRef.current);

    const resultsArray = collegeData?.results ?? [];
    if (!manualInputValue || resultsArray.length === 0) {
      setManualSuggestions([]);
      return;
    }

    manualSearchDebounceRef.current = setTimeout(() => {
      const suggestions = getAutocompleteSuggestions(manualInputValue, resultsArray, 12);
      setManualSuggestions(suggestions);
    }, 150);

    return () => {
      if (manualSearchDebounceRef.current) clearTimeout(manualSearchDebounceRef.current);
    };
  }, [manualInputValue, collegeData]);

  const colleges = useMemo(() => {
    return getManualProgressCollegeObjects(manualColleges);
  }, [manualColleges]);

  useEffect(() => {
    checkReminders();
  }, [checkReminders, colleges, generalCards]);

  const handleAddManualCollege = useCallback(() => {
    if (!selectedManualCollege) {
      setManualActionMessage('Search for a college, choose a match, then press Add.');
      return;
    }

    setManualColleges((prev) => {
      if (prev.includes(selectedManualCollege)) {
        setManualActionMessage(`${selectedManualCollege} is already in your final list.`);
        return prev;
      }

      setManualActionMessage(`${selectedManualCollege} added to your final list.`);
      return [...prev, selectedManualCollege];
    });
    setSelectedManualCollege('');
    setManualInputValue('');
    setManualSuggestions([]);
  }, [selectedManualCollege, setManualColleges]);

  const handleAddManualCollegeByName = useCallback((collegeName) => {
    const trimmedName = collegeName?.trim();
    if (!trimmedName) {
      setManualActionMessage('College not found. Try the full school name.');
      return;
    }

    setManualColleges((prev) => {
      if (prev.includes(trimmedName)) {
        setManualActionMessage(`${trimmedName} is already in your final list.`);
        return prev;
      }

      setManualActionMessage(`${trimmedName} added to your final list.`);
      return [...prev, trimmedName];
    });
    setSelectedManualCollege('');
    setManualInputValue('');
    setManualSuggestions([]);
  }, [setManualColleges]);

  const handleManualCollegeKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();

    const typedValue = manualInputValue.trim();
    const liveSuggestions = typedValue && collegeData?.results
      ? getAutocompleteSuggestions(typedValue, collegeData.results, 12)
      : [];
    const suggestions = manualSuggestions.length > 0 ? manualSuggestions : liveSuggestions;
    const exactSuggestion = manualSuggestions.find(
      (name) => name.toLowerCase() === typedValue.toLowerCase()
    );
    const liveExactSuggestion = liveSuggestions.find(
      (name) => name.toLowerCase() === typedValue.toLowerCase()
    );
    const collegeName = selectedManualCollege || exactSuggestion || liveExactSuggestion || suggestions[0];
    handleAddManualCollegeByName(collegeName);
  };

  const handleRemoveManualCollege = useCallback((collegeName) => {
    setManualColleges((prev) => prev.filter((name) => name !== collegeName));
  }, [setManualColleges]);

  const handleToggleGeneralItem = useCallback((cardId, itemId) => {
    setGeneralCards((prev) => prev.map((card) => (
      card.id === cardId
        ? {
          ...card,
          items: card.items.map((item) => (
            item.id === itemId ? { ...item, complete: !item.complete } : item
          )),
        }
        : card
    )));
  }, [setGeneralCards]);

  const handleUpdateGeneralItem = useCallback((cardId, itemId, updates) => {
    setGeneralCards((prev) => prev.map((card) => (
      card.id === cardId
        ? {
          ...card,
          items: card.items.map((item) => (
            item.id === itemId ? { ...item, ...updates, dateSource: 'manual' } : item
          )),
        }
        : card
    )));
  }, [setGeneralCards]);

  const handleAddGeneralItem = useCallback((cardId, text) => {
    setGeneralCards((prev) => prev.map((card) => (
      card.id === cardId
        ? {
          ...card,
          items: [
            ...card.items,
            { id: `general-item-${Date.now()}`, text, complete: false, date: '', reminderOffset: null, dateSource: 'manual' },
          ],
        }
        : card
    )));
  }, [setGeneralCards]);

  const handleDeleteGeneralItem = useCallback((cardId, itemId) => {
    setGeneralCards((prev) => prev.map((card) => (
      card.id === cardId
        ? { ...card, items: card.items.filter((item) => item.id !== itemId) }
        : card
    )));
  }, [setGeneralCards]);

  const handleDeleteGeneralCard = useCallback((cardId) => {
    setGeneralCards((prev) => prev.filter((card) => card.id !== cardId));
  }, [setGeneralCards]);

  const handleAddGeneralCard = useCallback(() => {
    const title = newGeneralCardTitle.trim();
    if (!title) return;
    setGeneralCards((prev) => [
      ...prev,
      {
        id: `general-card-${Date.now()}`,
        title,
        description: 'Add the steps you want to track for this part of your application.',
        items: [],
      },
    ]);
    setNewGeneralCardTitle('');
  }, [newGeneralCardTitle, setGeneralCards]);

  // Re-read application status whenever a college card changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const applicationStats = useMemo(() => getApplicationStats(colleges), [colleges, refreshKey]);

  const sortedColleges = useMemo(() => sortColleges(colleges, sortBy), [colleges, sortBy]);
  const manualNoOptionsText = collegeLoadError
    ? "Colleges couldn't load. Try refreshing the page."
    : manualInputValue.trim()
      ? 'College not found'
      : 'Start typing to search colleges';

  return (
    <div className="progress-page">
      <div className="progress-page__header">
        <h1 className="progress-page__title">Progress</h1>
        <p className="progress-page__subtitle">
          Progress is your final college list for applications. Add the colleges you are actively applying to here, then track tasks, deadlines, and submitted applications.
        </p>
      </div>

      <div className="progress-page__tabs">
        <button
          className={`progress-page__tab ${activeTab === 'final-list' ? 'progress-page__tab--active' : ''}`}
          onClick={() => setActiveTab('final-list')}
        >
          Final College List
        </button>
        <button
          className={`progress-page__tab ${activeTab === 'general' ? 'progress-page__tab--active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          General Progress
        </button>
        <button
          className={`progress-page__tab ${activeTab === 'individual' ? 'progress-page__tab--active' : ''}`}
          onClick={() => setActiveTab('individual')}
        >
          Individual Progress
        </button>
      </div>

      <div className="progress-page__content">
        {activeTab === 'general' && (
          <div className="progress-page__tab-panel">
            <div className="general-progress-hero">
              <div>
                <span className="progress-page__source-label">General progress</span>
                <h2>Application-wide checklist</h2>
                <p>
                  These cards follow the timeline guidance: recommendations, essays, testing,
                  financial aid, and after-submission checks. {graduationYear
                    ? 'Suggested dates use your graduation year; milestones that already passed start without a reminder. Edit any date to make it yours.'
                    : 'Choose your graduation year before MaiColleges suggests dates or reminders. You can still use and edit the checklist now.'}
                </p>
                {!graduationYear && (
                  <Link className="general-progress-hero__year-link" to="/my-info">
                    Choose graduation year in My Info
                  </Link>
                )}
              </div>
              <div className="general-progress-hero__count">
                <strong>{generalCards.filter((card) => card.items.length > 0 && card.items.every((item) => item.complete)).length}</strong>
                <span>complete</span>
              </div>
            </div>

            <div className="general-progress-add-card">
              <input
                value={newGeneralCardTitle}
                onChange={(event) => setNewGeneralCardTitle(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') handleAddGeneralCard();
                }}
                placeholder="Add your own card, like Interviews or Portfolio"
              />
              <button type="button" onClick={handleAddGeneralCard}>
                <Plus size={15} strokeWidth={2} />
                Add Card
              </button>
            </div>

            {generalCards.length > 0 ? (
              <div className="general-progress-grid">
                {generalCards.map((card) => (
                  <GeneralProgressCard
                    key={card.id}
                    card={card}
                    onToggleItem={handleToggleGeneralItem}
                    onUpdateItem={handleUpdateGeneralItem}
                    onAddItem={handleAddGeneralItem}
                    onDeleteItem={handleDeleteGeneralItem}
                    onDeleteCard={handleDeleteGeneralCard}
                  />
                ))}
              </div>
            ) : (
              <div className="progress-page__empty-state progress-page__empty-state--full">
                <p>No general progress cards yet</p>
                <p className="progress-page__empty-hint">Add a card above to track application-wide steps.</p>
              </div>
            )}
            </div>
        )}

        {activeTab === 'final-list' && (
          <div className="progress-page__tab-panel">
            <div className="progress-page__source-panel">
              <div className="progress-page__source-copy">
                <span className="progress-page__source-label">Final colleges</span>
                <strong>Your official application list</strong>
                <p>These are the colleges you are actively applying to. Home, Calendar, reminders, and Individual Progress use this list.</p>
              </div>
              <div className="progress-page__source-count">
                <strong>{colleges.length}</strong>
                <span>{colleges.length === 1 ? 'college' : 'colleges'}</span>
              </div>
            </div>

            <div className="progress-page__list-manager">
              <div className="progress-page__list-manager-header">
                <div>
                  <h2>Final College List</h2>
                  <p>Add or remove schools here as your real application list changes.</p>
                </div>
              </div>

              {colleges.length > 0 && (
                <div className="progress-page__college-chips" aria-label="Final college list">
                  {colleges.map((college) => (
                    <div className="progress-page__college-chip" key={college.name}>
                      <span>{college.name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveManualCollege(college.name)}
                        aria-label={`Remove ${college.name} from final college list`}
                        title={`Remove ${college.name}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="progress-page__manual-add">
              <Autocomplete
                  disablePortal
                  options={manualSuggestions}
                  loading={collegeLoading}
                  loadingText="Loading colleges..."
                  noOptionsText={manualNoOptionsText}
                  inputValue={manualInputValue}
                  value={selectedManualCollege || null}
                  onInputChange={(event, newInputValue) => {
                    setManualInputValue(newInputValue);
                    setManualActionMessage('');
                    if (!newInputValue) setSelectedManualCollege('');
                  }}
                  onChange={(event, newValue) => {
                    setSelectedManualCollege(newValue || '');
                    setManualInputValue(newValue || '');
                    setManualActionMessage('');
                  }}
                  filterOptions={(options) => options}
                  sx={{
                    width: 340,
                    '& .MuiOutlinedInput-root': {
                      fontFamily: "var(--ds-font-family, 'Poppins', sans-serif)",
                      fontSize: '0.88rem',
                      borderRadius: '10px',
                      backgroundColor: 'var(--ds-surface)',
                      color: 'var(--ds-primary-black)',
                      padding: '3px 10px',
                      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                      '& fieldset': {
                        borderColor: 'var(--ds-border)',
                        borderWidth: '1px',
                        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                      },
                      '&:hover fieldset': {
                        borderColor: 'var(--ds-primary-300)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'var(--ds-active-color)',
                        borderWidth: '1.5px',
                        boxShadow: '0 0 0 3px rgba(43, 122, 232, 0.08)',
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontFamily: "var(--ds-font-family, 'Poppins', sans-serif)",
                      fontSize: '0.88rem',
                      color: 'var(--ds-primary-black)',
                      '&::placeholder': {
                        color: 'var(--ds-text-muted)',
                        opacity: 1,
                      },
                    },
                    '& .MuiAutocomplete-popupIndicator': {
                      color: 'var(--ds-text-muted)',
                    },
                    '& .MuiAutocomplete-clearIndicator': {
                      color: 'var(--ds-text-muted)',
                    },
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        fontFamily: "var(--ds-font-family, 'Poppins', sans-serif)",
                        fontSize: '0.88rem',
                        borderRadius: '10px',
                        border: '1px solid var(--ds-border)',
                        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
                        backgroundColor: 'var(--ds-surface)',
                        marginTop: '6px',
                        '& .MuiAutocomplete-listbox': {
                          padding: '6px',
                          '& .MuiAutocomplete-option': {
                            fontFamily: "var(--ds-font-family, 'Poppins', sans-serif)",
                            fontSize: '0.88rem',
                            borderRadius: '6px',
                            padding: '8px 12px',
                            color: 'var(--ds-primary-black)',
                            '&:hover': {
                              backgroundColor: 'var(--ds-surface-hover)',
                            },
                            '&[aria-selected="true"]': {
                              backgroundColor: 'var(--ds-active-bg)',
                              color: 'var(--ds-active-color)',
                            },
                          },
                        },
                        '& .MuiAutocomplete-noOptions': {
                          fontFamily: "var(--ds-font-family, 'Poppins', sans-serif)",
                          fontSize: '0.85rem',
                          color: 'var(--ds-text-muted)',
                        },
                        '& .MuiAutocomplete-loading': {
                          fontFamily: "var(--ds-font-family, 'Poppins', sans-serif)",
                          fontSize: '0.85rem',
                          color: 'var(--ds-text-muted)',
                        },
                      },
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search for a college"
                      onKeyDown={handleManualCollegeKeyDown}
                    />
                  )}
              />
              <button className="progress-page__manual-add-btn" onClick={handleAddManualCollege}>
                Add
              </button>
              {manualActionMessage && (
                <p className="progress-page__manual-message" role="status" aria-live="polite">
                  {manualActionMessage}
                </p>
              )}
            </div>

            {colleges.length === 0 && (
              <div className="progress-page__empty-state">
                <p>Build your final college list</p>
                <p className="progress-page__empty-hint">
                  Search above to add the schools you are actively applying to.
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'individual' && (
          <div className="progress-page__tab-panel">
            <div className="progress-page__source-panel">
              <div className="progress-page__source-copy">
                <span className="progress-page__source-label">Individual progress</span>
                <strong>Track colleges after they are in your final list</strong>
                <p>Add or remove schools in the Final College List tab first. This view only tracks application dates, submitted status, reminders, and tasks for those colleges.</p>
              </div>
              <div className="progress-page__source-count">
                <strong>{colleges.length}</strong>
                <span>{colleges.length === 1 ? 'college' : 'colleges'}</span>
              </div>
            </div>

            {/* Summary bar */}
            {colleges.length > 0 && (
              <div className="summary-bar">
                <div className="summary-bar__tiles">
                  <div className="summary-tile">
                    <div className="summary-tile__icon summary-tile__icon--total">
                      <FileText size={18} />
                    </div>
                    <div>
                      <div className="summary-tile__count">{applicationStats.total}</div>
                      <div className="summary-tile__label">Applications</div>
                    </div>
                  </div>
                  <div className="summary-tile">
                    <div className="summary-tile__icon summary-tile__icon--pending">
                      <Clock size={18} />
                    </div>
                    <div>
                      <div className="summary-tile__count">{applicationStats.inProgress}</div>
                      <div className="summary-tile__label">In Progress</div>
                    </div>
                  </div>
                  <div className="summary-tile">
                    <div className="summary-tile__icon summary-tile__icon--complete">
                      <CheckCircle2 size={18} />
                    </div>
                    <div>
                      <div className="summary-tile__count">{applicationStats.submitted}</div>
                      <div className="summary-tile__label">Submitted</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Sort controls */}
            {colleges.length > 1 && (
              <div className="progress-page__sort-bar">
                <span className="progress-page__sort-label">Sort by</span>
                <div className="progress-page__sort-options">
                  {SORT_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      className={`progress-page__sort-btn ${sortBy === opt.value ? 'progress-page__sort-btn--active' : ''}`}
                      onClick={() => setSortBy(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* College cards */}
            <div className="progress-page__cards">
              {colleges.length === 0 ? (
                <div className="progress-page__empty-state">
                  <p>
                    Start with your Final College List
                  </p>
                  <p className="progress-page__empty-hint">
                    Add the schools you are applying to in the first tab, then come back here to track tasks, deadlines, and submitted status.
                  </p>
                </div>
              ) : (
                sortedColleges.map((college) => (
                  <CollegeCard
                    key={college.name}
                    college={college}
                    onUpdate={triggerRefresh}
                    onTaskChange={checkReminders}
                  />
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProgressPage;
